import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useCmsPage, c } from '../lib/cms';

const articleJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'Article',
  headline: 'Best Dental Analytics Platforms for Multi-Location Groups — 2026',
  description:
    'Top dental analytics platforms for multi-location DSOs in 2026: RevTech InsightFlow, Dental Intelligence, Jarvis Analytics, Planet DDS Analytics, and NDS DataVault + Power BI. Compared by analytics depth, RCM focus, and enterprise features.',
  datePublished: '2026-01-01',
  dateModified: '2026-05-01',
  author: { '@type': 'Organization', name: 'RevTech Partners' },
  publisher: { '@type': 'Organization', name: 'RevTech Partners', url: 'https://revtechpartners.com' },
  articleSection: 'Dental Analytics',
};

const faqJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'What is the best dental analytics platform for a DSO?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'The best platform depends on what you\'re trying to measure. For revenue cycle analytics — AR aging, denial rates, payment reconciliation, payer performance — RevTech InsightFlow is purpose-built for DSOs with multi-location roll-up and deep RCM data. For production and scheduling analytics, Dental Intelligence has broad adoption and strong scheduling optimization features. For enterprise cross-system reporting on NDS-managed data, Power BI connected to NDS DataVault gives the most flexibility.',
      },
    },
    {
      '@type': 'Question',
      name: 'What is the difference between production analytics and revenue cycle analytics for dental?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Production analytics focus on what clinical activity happened in the practice: scheduled vs. completed procedures, case acceptance rate, provider productivity, appointment fill rate, recall performance. Revenue cycle analytics focus on what happened after the appointment: claim submission, adjudication, denial categorization, payment posting, AR aging, and net collection rate. Both are important for a DSO, but they answer different questions and require different data.',
      },
    },
    {
      '@type': 'Question',
      name: 'Can a dental analytics platform replace a data warehouse?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Not typically. Analytics platforms are built for user-facing reporting and dashboards. A data warehouse is the infrastructure that stores, normalizes, and governs the underlying data. For DSOs with multiple PMS systems, a data warehouse like NDS DataVault is necessary to ensure that analytics tools are working from clean, consistent, normalized data rather than pulling separately from each PMS in an uncoordinated way.',
      },
    },
    {
      '@type': 'Question',
      name: 'Does RevTech InsightFlow work for DSOs with 50 or 100+ locations?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Yes. InsightFlow is designed specifically for multi-location AR analytics at DSO scale. It provides enterprise roll-up across all locations with drill-down by location, provider, payer, and CDT code. Because it is connected to the NDS DataVault layer, it can aggregate claim and payment data across every PMS in your portfolio regardless of which vendor each location uses.',
      },
    },
  ],
};

const analyticsTypes = [
  {
    type: 'Production & Operations Analytics',
    icon: '🦷',
    color: '#f57c00',
    description:
      'These platforms analyze what happens in the operatory: procedures scheduled, completed, and accepted; provider productivity; schedule utilization; case acceptance rate; patient recall performance. The data source is the PMS appointment and procedure ledger.',
    platforms: ['Dental Intelligence', 'Jarvis Analytics', 'Planet DDS Analytics'],
    limitations:
      'Strong for clinical operations visibility but typically do not include claim-level data, AR aging, denial analytics, or payment reconciliation. Not designed for revenue cycle management.',
  },
  {
    type: 'Revenue Cycle Analytics',
    icon: '📊',
    color: '#6C63FF',
    description:
      'These platforms analyze what happens after the appointment: claim submission, adjudication, denial categorization, ERA/EOB matching, AR aging, and net collection rate. The data source is the insurance claim and payment record.',
    platforms: ['RevTech InsightFlow', 'RevTech DenialIQ', 'RevTech ReconIQ', 'Zentist'],
    limitations:
      'Focused on insurance billing and AR — not designed for production scheduling optimization or clinical operations reporting.',
  },
  {
    type: 'Data Warehouse + BI',
    icon: '🗄️',
    color: '#1565c0',
    description:
      'These approaches use a dental data warehouse (NDS DataVault) to normalize all PMS and RCM data, then connect enterprise BI tools (Power BI, Tableau, Looker) to build fully custom analytics layers that can span production, RCM, finance, and HR data.',
    platforms: ['NDS DataVault + Power BI', 'NDS DataVault + Tableau', 'Custom BI on normalized data'],
    limitations:
      'Most powerful and flexible, but requires an analytics or data team to build and maintain reports. Higher implementation investment than pre-built dashboards.',
  },
];

const platforms = [
  {
    name: 'RevTech InsightFlow',
    category: 'Revenue Cycle Analytics',
    tagColor: '#6C63FF',
    strengths:
      'Enterprise AR analytics purpose-built for multi-location dental. Claim aging by payer, provider, and location. Net collection rate tracking. Outstanding claim drill-down. Integrates natively with DenialIQ, ReconIQ, ClaimVision, and PayerIQ for a full revenue cycle view. Connected to NDS DataVault for cross-PMS data.',
    enterpriseFeatures: 'Multi-location roll-up, role-based access (AR specialist vs. DSO executive views), payer benchmarking, denial trend analytics.',
    bestFor: 'DSOs with in-house billing teams that need enterprise-level AR visibility and a structured denial management workflow.',
  },
  {
    name: 'Dental Intelligence',
    category: 'Production & Patient Analytics',
    tagColor: '#f57c00',
    strengths:
      'Leading production analytics platform with widespread DSO adoption. Scheduling optimization, case acceptance tracking, patient engagement automation (recalls, reactivations, reminders), and provider performance dashboards. Strong consumer brand and implementation support.',
    enterpriseFeatures: 'Multi-location roll-up for production KPIs, scheduling benchmarks, regional performance comparisons.',
    bestFor: 'DSOs focused on production optimization, schedule fill rates, and patient engagement — not primarily concerned with insurance AR analytics.',
  },
  {
    name: 'Jarvis Analytics',
    category: 'Operations Reporting',
    tagColor: '#00695c',
    strengths:
      'Multi-PMS operational reporting platform. Connects to Dentrix, Eaglesoft, Open Dental, and other systems for standardized KPI reporting across a DSO portfolio. Used by mid-market and large DSOs for operational performance visibility without a full data warehouse build.',
    enterpriseFeatures: 'Multi-PMS connectivity, standardized location benchmarking, provider productivity reporting, payer mix analysis.',
    bestFor: 'DSOs that need standardized operational KPI reporting across multiple PMS systems without building a custom data infrastructure.',
  },
  {
    name: 'Planet DDS Analytics',
    category: 'Cloud PMS + Analytics',
    tagColor: '#1565c0',
    strengths:
      'Analytics layer native to the Planet DDS (Denticon) cloud PMS. Provides production, collections, and appointment reporting within the Planet DDS ecosystem. Works seamlessly if the entire DSO portfolio is on Planet DDS.',
    enterpriseFeatures: 'Native to Planet DDS — no separate integration required. Multi-location roll-up within the Denticon environment.',
    bestFor: 'DSOs that are fully standardized on Planet DDS and want built-in reporting without a separate analytics product.',
  },
  {
    name: 'NDS DataVault + Power BI',
    category: 'Data Warehouse + Enterprise BI',
    tagColor: '#2D3250',
    strengths:
      'The most flexible and powerful option for large DSOs. NDS DataVault normalizes all PMS and RCM data into a single enterprise schema. Power BI (or Tableau, Looker) sits on top for fully custom dashboards. Can combine clinical, financial, HR, and payer data in a single analytics environment. This is the infrastructure layer that also powers RevTech analytics.',
    enterpriseFeatures: 'Unlimited custom KPIs, raw data access, cross-system joins, historical trend analysis, PE investor reporting, custom role-based dashboards.',
    bestFor: 'DSOs at 20+ locations, PE-backed groups with investor reporting requirements, or any organization that needs custom KPIs beyond what pre-built dashboards provide.',
  },
];

const scaleConsiderations = [
  {
    scale: '1–10 Locations',
    icon: '🏠',
    color: '#43a047',
    needs: [
      'Single PMS platform is common — built-in reports may be adequate',
      'Dental Intelligence or Jarvis for production visibility',
      'RevTech for billing teams managing multi-payer insurance AR',
      'Data warehouse may be premature investment at this scale',
    ],
  },
  {
    scale: '10–50 Locations',
    icon: '🏢',
    color: '#f57c00',
    needs: [
      'Mixed PMS environments begin to appear from acquisitions',
      'Standardized KPI reporting becomes critical for leadership visibility',
      'Denial rates and net collection rate variance by location becomes actionable',
      'Data warehouse begins to pay off — consider NDS DataVault',
      'RevTech InsightFlow for enterprise AR roll-up',
    ],
  },
  {
    scale: '50–100+ Locations',
    icon: '🏙️',
    color: '#6C63FF',
    needs: [
      'Multiple PMS systems in portfolio — normalization is non-negotiable',
      'PE investors require enterprise-grade financial reporting',
      'Custom KPIs and board-ready dashboards required',
      'NDS DataVault + Power BI for full custom analytics flexibility',
      'RevTech suite for revenue cycle — DenialIQ, ReconIQ, ClaimVision, PayerIQ, InsightFlow',
      'Data governance and audit trails for compliance',
    ],
  },
];

export default function BestDentalAnalyticsPage() {
  const cmsPage = useCmsPage('xentrios', 'best-dental-analytics');
  const heroHeadline = c(cmsPage, 'hero', 'headline', 'Best Dental Analytics Platforms for Multi-Location Groups');
  const heroSubtitle = c(cmsPage, 'hero', 'subtitle', 'A structured comparison of the leading dental analytics platforms for U.S. DSOs in 2026 — organized by analytics type, enterprise features, and the right fit at different organizational scales.');
  const analyticsTypeItems = c(cmsPage, 'features', 'items', analyticsTypes);
  const platformItems = c(cmsPage, 'benefits', 'items', platforms);
  const scaleItems = c(cmsPage, 'workflow', 'items', scaleConsiderations);
  return (
    <div>
      <Helmet>
        <title>Best Dental Analytics Platforms for Multi-Location Groups — 2026</title>
        <meta
          name="description"
          content="Top dental analytics platforms for multi-location DSOs in 2026: RevTech InsightFlow, Dental Intelligence, Jarvis Analytics, Planet DDS Analytics, and NDS DataVault + Power BI. Compared by analytics depth, RCM focus, and enterprise features."
        />
        <script type="application/ld+json">{JSON.stringify(articleJsonLd)}</script>
        <script type="application/ld+json">{JSON.stringify(faqJsonLd)}</script>
      </Helmet>

      {/* Hero */}
      <section className="xen-hero">
        <div className="xen-container">
          <p className="xen-hero-eyebrow">Dental Analytics Platform Guide</p>
          <h1>{heroHeadline}</h1>
          <p className="subtitle">{heroSubtitle}</p>
          <div className="xen-hero-actions">
            <Link to="/demo" className="xen-btn xen-btn-primary" style={{ padding: '0.75rem 2rem', fontSize: '1rem' }}>
              Request a Demo
            </Link>
            <Link to="/stack" className="xen-btn xen-btn-outline" style={{ padding: '0.75rem 2rem', fontSize: '1rem', borderColor: '#fff', color: '#fff' }}>
              See the Full Stack
            </Link>
          </div>
        </div>
      </section>

      {/* Two types of dental analytics */}
      <section className="xen-section xen-section-light">
        <div className="xen-container">
          <p className="xen-section-eyebrow">Market Structure</p>
          <h2>Three types of dental analytics</h2>
          <p className="xen-section-subtitle" style={{ marginBottom: '2.5rem' }}>
            The dental analytics market contains platforms that solve very different problems.
            Choosing the right one starts with understanding which type of analytics you actually need.
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '1.5rem' }}>
            {analyticsTypeItems.map((t) => (
              <div
                key={t.type}
                style={{
                  borderTop: `4px solid ${t.color}`,
                  padding: '1.75rem',
                  background: '#fff',
                  boxShadow: '0 2px 10px rgba(0,0,0,0.07)',
                  borderRadius: '8px',
                }}
              >
                <div style={{ fontSize: '2rem', marginBottom: '0.75rem' }}>{t.icon}</div>
                <h3 style={{ color: t.color, marginBottom: '0.5rem' }}>{t.type}</h3>
                <p style={{ fontSize: '0.93rem', color: '#444', marginBottom: '1rem' }}>{t.description}</p>
                <p style={{ fontSize: '0.8rem', fontWeight: 700, color: '#666', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '0.35rem' }}>
                  Platforms in this category
                </p>
                <ul style={{ paddingLeft: '1.1rem', fontSize: '0.88rem', color: '#555', lineHeight: 1.7, marginBottom: '1rem' }}>
                  {t.platforms.map((pl, i) => <li key={i}>{pl}</li>)}
                </ul>
                <p style={{ fontSize: '0.85rem', color: '#888', fontStyle: 'italic' }}>{t.limitations}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Platform comparison */}
      <section className="xen-section" style={{ background: '#fff' }}>
        <div className="xen-container">
          <p className="xen-section-eyebrow">Platform Comparison</p>
          <h2>Top dental analytics platforms compared</h2>
          <p className="xen-section-subtitle" style={{ marginBottom: '2.5rem' }}>
            Five platforms DSOs evaluate for enterprise analytics in 2026.
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '1.5rem' }}>
            {platformItems.map((p) => (
              <div
                key={p.name}
                style={{
                  borderTop: `4px solid ${p.tagColor}`,
                  padding: '1.75rem',
                  background: '#fafafa',
                  boxShadow: '0 2px 12px rgba(0,0,0,0.07)',
                  borderRadius: '8px',
                }}
              >
                <h3 style={{ margin: '0 0 0.25rem', color: 'var(--xen-navy)' }}>{p.name}</h3>
                <p style={{ fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: '0.06em', color: p.tagColor, fontWeight: 700, marginBottom: '1rem' }}>
                  {p.category}
                </p>
                <p style={{ fontSize: '0.93rem', color: '#444', marginBottom: '0.75rem' }}>{p.strengths}</p>
                <p style={{ fontSize: '0.85rem', color: '#555', marginBottom: '1rem' }}>
                  <strong style={{ color: 'var(--xen-navy)' }}>Enterprise features: </strong>
                  {p.enterpriseFeatures}
                </p>
                <p style={{ fontSize: '0.9rem', fontWeight: 600, color: 'var(--xen-navy)' }}>
                  Best for: <span style={{ fontWeight: 400, color: '#555' }}>{p.bestFor}</span>
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Multi-location requirements */}
      <section className="xen-section xen-section-light">
        <div className="xen-container">
          <p className="xen-section-eyebrow">Scale Considerations</p>
          <h2>Multi-location analytics requirements by scale</h2>
          <p className="xen-section-subtitle" style={{ marginBottom: '2.5rem' }}>
            What you need from a dental analytics platform changes significantly as your DSO grows.
            Here is how requirements evolve at key scale thresholds.
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '1.5rem' }}>
            {scaleItems.map((s) => (
              <div
                key={s.scale}
                style={{
                  borderLeft: `4px solid ${s.color}`,
                  padding: '1.5rem',
                  background: '#fff',
                  boxShadow: '0 1px 8px rgba(0,0,0,0.06)',
                  borderRadius: '0 8px 8px 0',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem' }}>
                  <span style={{ fontSize: '1.75rem' }}>{s.icon}</span>
                  <h3 style={{ margin: 0, color: s.color }}>{s.scale}</h3>
                </div>
                <ul style={{ paddingLeft: '1.1rem', fontSize: '0.92rem', color: '#444', lineHeight: 1.8 }}>
                  {s.needs.map((n, i) => <li key={i}>{n}</li>)}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="xen-section xen-section-dark xen-section-center">
        <div className="xen-container">
          <h2>See RevTech analytics for your DSO.</h2>
          <p className="xen-section-subtitle" style={{ marginTop: '1rem', marginBottom: '2rem' }}>
            Talk to a solutions engineer about your current analytics stack, PMS environment,
            and scale. We'll show you exactly where RevTech InsightFlow and NDS DataVault fit.
          </p>
          <Link to="/demo" className="xen-btn xen-btn-primary" style={{ padding: '0.875rem 2.5rem', fontSize: '1.05rem' }}>
            Request a Demo
          </Link>
        </div>
      </section>
    </div>
  );
}
